import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  TrashIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  DocumentArrowDownIcon,
  CameraIcon,
  PlusIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { DatePicker } from '../components/ui/date-picker';
import BankCombobox from '../components/BankCombobox';
import AccountCombobox from '../components/AccountCombobox';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '../components/ui/breadcrumb';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { cn } from '../lib/utils';
import { Tip } from '../components/ui/tip';
import { openPdf } from '../utils/pdfHelpers';
import { disconnectAppSocket, getAppSocket } from '../lib/wesSocket';
import { confirmDialog } from '../components/ConfirmDialog';
import { parseMoneyAr } from '../lib/money';

type CheckType = 'received' | 'issued';

interface CheckRecord {
  id: string;
  type: CheckType;
  issueDate?: string | null;
  receivedDate?: string | null;
  deliveryDate?: string | null;
  dueDate?: string | null;
  checkNumber?: string | null;
  bank?: string | null;
  amount: number;
  emitterName?: string | null;
  clientName?: string | null;
  providerName?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  status: string;
  originType?: string | null;
  originNumber?: string | null;
  photoUrl?: string | null;
  notes?: string | null;
  endorsedToName?: string | null;
  endorsedAccountId?: string | null;
}

interface CheckForm {
  type: CheckType;
  issueDate: string;
  receivedDate: string;
  deliveryDate: string;
  dueDate: string;
  checkNumber: string;
  bank: string;
  amount: string;
  emitterName: string;
  clientName: string;
  providerName: string;
  contactName: string;
  contactPhone: string;
  notes: string;
  status: string;
  originType: string;
  originNumber: string;
}

interface SheetConfig {
  enabled: boolean;
  sheetUrl?: string | null;
  embedUrl?: string | null;
}

const DEFAULT_STATUS_OPTIONS = [
  { id: 'pending', label: 'En cartera' },
  { id: 'deposited', label: 'Depositado' },
  { id: 'cleared', label: 'Acreditado' },
  { id: 'rejected', label: 'Rechazado' },
  { id: 'repudiated', label: 'Repudiado' },
  { id: 'endorsed', label: 'Endosado' },
  { id: 'sold', label: 'Vendido' },
];

const formatBankName = (value?: string | null) => {
  if (!value) return '—';
  let label = value.trim();
  if (!label) return '—';
  if (label.toLowerCase().startsWith('banco_')) {
    label = label.slice(6);
  }
  label = label.replace(/_/g, ' ');
  return label
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const STATUS_BADGES: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800',
  deposited: 'bg-blue-100 text-blue-800',
  cleared: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
  repudiated: 'bg-orange-100 text-orange-800',
  endorsed: 'bg-purple-100 text-purple-800',
  sold: 'bg-indigo-100 text-indigo-800',
};

const EMPTY_FORM: CheckForm = {
  type: 'received',
  issueDate: '',
  receivedDate: '',
  deliveryDate: '',
  dueDate: '',
  checkNumber: '',
  bank: '',
  amount: '',
  emitterName: '',
  clientName: '',
  providerName: '',
  contactName: '',
  contactPhone: '',
  notes: '',
  status: 'pending',
  originType: '',
  originNumber: '',
};

const formatCurrency = (value: number = 0) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 2,
  }).format(value || 0);

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  try {
    return new Date(`${value}T00:00:00`).toLocaleDateString('es-AR');
  } catch (error) {
    return value;
  }
};

const getDaysToDue = (dueDate?: string | null) => {
  if (!dueDate) return null;
  const due = new Date(`${dueDate}T00:00:00`);
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffMs = due.getTime() - today.getTime();
  return Math.round(diffMs / 86400000);
};

const formatDaysToDue = (days: number | null) => {
  if (days === null) return 'Sin fecha';
  if (days === 0) return 'Hoy';
  if (days === 1) return 'Mañana';
  if (days < 0) {
    const overdue = Math.abs(days);
    return overdue === 1 ? '1 día' : `${overdue} días`;
  }
  if (days >= 30) {
    const months = Math.round(days / 30);
    return months === 1 ? '1 mes' : `${months} meses`;
  }
  return `${days} días`;
};

const getDueBadgeClass = (days: number | null) => {
  if (days === null) return 'bg-slate-100 text-slate-800';
  if (days < 0) return 'bg-red-100 text-red-800';
  if (days <= 7) return 'bg-amber-100 text-amber-800';
  return 'bg-emerald-100 text-emerald-800';
};

const formatOrigin = (originType?: string | null, originNumber?: string | null) => {
  if (!originType && !originNumber) return '—';
  const label = originType === 'factura' ? 'Factura' : originType === 'remito' ? 'Remito' : 'Origen';
  return originNumber ? `${label} ${originNumber}` : label;
};

const CashChecks: React.FC = () => {
  const navigate = useNavigate();
  const [receivedChecks, setReceivedChecks] = useState<CheckRecord[]>([]);
  const [issuedChecks, setIssuedChecks] = useState<CheckRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTable, setActiveTable] = useState<CheckType>('received');
  const [searchTerm, setSearchTerm] = useState('');
  const [bankFilter, setBankFilter] = useState('all');
  const [dueFilter, setDueFilter] = useState('all');
  const [uploadingPhotoId, setUploadingPhotoId] = useState<string | null>(null);
  const [ctaAccounts, setCtaAccounts] = useState<Array<{ id: string; entityId: string; displayName: string; entityType: string; currentBalance: number }>>([]);
  const [ctaEntities, setCtaEntities] = useState<Array<{ id: string; displayName: string; entityType: string }>>([]);
  // Endoso: a quién se le pasa el cheque
  const [endorsingCheck, setEndorsingCheck] = useState<CheckRecord | null>(null);
  const [endorseAccountId, setEndorseAccountId] = useState('');
  const [endorseApply, setEndorseApply] = useState(true);
  const [endorsing, setEndorsing] = useState(false);
  const [formAccountId, setFormAccountId] = useState('');
  const [applyToAccount, setApplyToAccount] = useState(true);
  const [statusOptions, setStatusOptions] = useState(DEFAULT_STATUS_OPTIONS);
  const [sheetConfig, setSheetConfig] = useState<SheetConfig>({ enabled: false });
  const [syncingSheet, setSyncingSheet] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState<CheckForm>(EMPTY_FORM);
  const [editingCheck, setEditingCheck] = useState<CheckRecord | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Selección y endoso a proveedor (solo cheques recibidos en cartera)
  const [selectedCheckIds, setSelectedCheckIds] = useState<Set<string>>(new Set());
  const [providers, setProviders] = useState<{ id: string; name: string; companyName?: string | null }[]>([]);
  const [endorseDialogOpen, setEndorseDialogOpen] = useState(false);
  const [endorseProviderId, setEndorseProviderId] = useState<string>('');
  const [endorseNotes, setEndorseNotes] = useState<string>('');
  const [endorsingNow, setEndorsingNow] = useState(false);
  const refreshTimeoutRef = useRef<number | null>(null);
  const fetchChecksRef = useRef<null | ((options?: { silent?: boolean }) => void)>(null);

  const fetchConfig = async () => {
    try {
      const response = await axios.get('/api/checks/config');
      if (response.data?.statuses?.length) {
        setStatusOptions(response.data.statuses);
      }
      if (response.data?.sheet) {
        setSheetConfig(response.data.sheet);
      }
    } catch (error) {
      console.error('Error obteniendo configuración de cheques:', error);
    }
  };

  const fetchChecks = async (options?: { silent?: boolean }) => {
    const silent = Boolean(options?.silent);
    if (!silent) setLoading(true);
    const sharedParams: Record<string, string> = {
      orderBy: 'dueDate',
      orderDir: 'asc',
    };

    try {
      const [receivedRes, issuedRes] = await Promise.all([
        axios.get('/api/checks', { params: { ...sharedParams, type: 'received' } }),
        axios.get('/api/checks', { params: { ...sharedParams, type: 'issued' } }),
      ]);
      setReceivedChecks(receivedRes.data?.checks || []);
      setIssuedChecks(issuedRes.data?.checks || []);
    } catch (error) {
      console.error('Error obteniendo cheques:', error);
      if (!silent) toast.error('No pudimos cargar los cheques');
    } finally {
      if (!silent) setLoading(false);
    }
  };
  fetchChecksRef.current = fetchChecks;

  // Los cambios se pintan en el acto sobre la fila y después el socket
  // reconcilia en silencio: recargar con spinner se siente como un refresh.
  const applyLocalCheck = (checkId: string, patch: Partial<CheckRecord>) => {
    const merge = (list: CheckRecord[]) =>
      list.map((c) => (c.id === checkId ? { ...c, ...patch } : c));
    setReceivedChecks(merge);
    setIssuedChecks(merge);
  };

  const removeLocalCheck = (checkId: string) => {
    const drop = (list: CheckRecord[]) => list.filter((c) => c.id !== checkId);
    setReceivedChecks(drop);
    setIssuedChecks(drop);
  };

  useEffect(() => {
    fetchConfig();
    fetchChecks();
    fetchProviders();
    fetchCtaAccounts();
  }, []);

  useEffect(() => {
    const scheduleSilentRefresh = () => {
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current);
      }
      refreshTimeoutRef.current = window.setTimeout(() => {
        fetchChecksRef.current?.({ silent: true });
      }, 350);
    };

    const socket = getAppSocket();
    socket.on('cash:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      socket.off('cash:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
      disconnectAppSocket();
    };
  }, []);

  useEffect(() => {
    if (!formOpen) {
      setEditingCheck(null);
      setFormData(EMPTY_FORM);
    }
  }, [formOpen]);

  const statusLabel = (status: string) =>
    statusOptions.find((option) => option.id === status)?.label || status;

  const handleOpenForm = (check?: CheckRecord) => {
    if (check) {
      setEditingCheck(check);
      setFormData({
        type: check.type,
        issueDate: check.issueDate || '',
        receivedDate: check.receivedDate || '',
        deliveryDate: check.deliveryDate || '',
        dueDate: check.dueDate || '',
        checkNumber: check.checkNumber || '',
        bank: check.bank || '',
        amount: check.amount?.toString() || '',
        emitterName: check.emitterName || '',
        clientName: check.clientName || '',
        providerName: check.providerName || '',
        contactName: check.contactName || '',
        contactPhone: check.contactPhone || '',
        notes: check.notes || '',
        status: check.status,
        originType: check.originType || '',
        originNumber: check.originNumber || '',
      });
    } else {
      setFormData(EMPTY_FORM);
      setEditingCheck(null);
    }
    setFormOpen(true);
  };

  const handleFormChange = (field: keyof CheckForm, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!formData.dueDate) {
      toast.error('La fecha de cobro es obligatoria');
      return;
    }
    const amountValue = parseMoneyAr(formData.amount);
    if (Number.isNaN(amountValue) || amountValue <= 0) {
      toast.error('El monto debe ser mayor a 0');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        ...formData,
        amount: amountValue,
        ...(formAccountId
          ? formAccountId.startsWith('entity:')
            ? { entityId: formAccountId.split(':')[2], entityType: formAccountId.split(':')[1], applyToAccount }
            : { accountId: formAccountId, applyToAccount }
          : {})
      };
      if (editingCheck) {
        await axios.put(`/api/checks/${editingCheck.id}`, payload);
        toast.success('Cheque actualizado');
      } else {
        const createRes = await axios.post('/api/checks', payload);
        if (createRes.data?.accountPayment?.applied) {
          toast.success('Cheque creado y pago registrado en la cuenta corriente');
        } else {
          toast.success('Cheque creado');
        }
        setFormAccountId('');
        setApplyToAccount(true);
      }
      setFormOpen(false);
      fetchChecks({ silent: true });
    } catch (error) {
      console.error('Error guardando cheque:', error);
      toast.error('No pudimos guardar el cheque');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (checkId: string) => {
    const confirmed = await confirmDialog('¿Eliminar este cheque? Esta acción no se puede deshacer.');
    if (!confirmed) return;

    try {
      setDeletingId(checkId);
      await axios.delete(`/api/checks/${checkId}`);
      removeLocalCheck(checkId);
      toast.success('Cheque eliminado');
    } catch (error) {
      console.error('Error eliminando cheque:', error);
      toast.error('No pudimos eliminar el cheque');
    } finally {
      setDeletingId(null);
    }
  };

  const handleStatusChange = async (check: CheckRecord, newStatus: string) => {
    if (check.status === newStatus) return;
    // Endosar un cheque recibido pide a quién se le pasa
    if (newStatus === 'endorsed' && check.type === 'received') {
      setEndorseAccountId('');
      setEndorseApply(true);
      setEndorsingCheck(check);
      return;
    }
    const estadoPrevio = check.status;
    try {
      setUpdatingStatusId(check.id);
      applyLocalCheck(check.id, { status: newStatus });
      const res = await axios.patch(`/api/checks/${check.id}/status`, { status: newStatus });
      if (res.data?.check) applyLocalCheck(check.id, res.data.check);
      const esRebote = newStatus === 'rejected' || newStatus === 'repudiated';
      const verbo = newStatus === 'repudiated' ? 'repudiado' : 'rechazado';
      if (esRebote && res.data?.chargeBack?.charged) {
        toast.success(`Cheque ${verbo}: la deuda se volvió a cargar en la cuenta corriente`);
      } else if (esRebote && check.type === 'received') {
        toast.warning(`Cheque ${verbo}. No está vinculado a una cuenta corriente — cargá el cargo a mano si corresponde.`);
      } else {
        toast.success('Estado actualizado');
      }
    } catch (error) {
      console.error('Error actualizando estado del cheque:', error);
      applyLocalCheck(check.id, { status: estadoPrevio });
      toast.error('No se pudo actualizar el estado');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handlePhotoUpload = async (check: CheckRecord, file: File) => {
    try {
      setUploadingPhotoId(check.id);
      const formData = new FormData();
      formData.append('photo', file);
      const res = await axios.post(`/api/checks/${check.id}/photo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data?.check) applyLocalCheck(check.id, res.data.check);
      toast.success('Foto del cheque adjuntada');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo subir la foto');
    } finally {
      setUploadingPhotoId(null);
    }
  };

  const fetchCtaAccounts = async () => {
    try {
      const res = await axios.get('/api/current-accounts', { params: { entityType: 'all' } });
      const list = Array.isArray(res.data?.accounts) ? res.data.accounts : [];
      setCtaAccounts(list.map((a: any) => ({
        id: a.id,
        entityId: a.entityId || a.clientId || '',
        displayName: a.displayName || a.clientName || 'Cuenta',
        entityType: a.entityType || 'client',
        currentBalance: Number(a.currentBalance) || 0
      })));
      // Clientes y proveedores sin cta cte también se pueden elegir (la cuenta se crea sola)
      const [cr, pr] = await Promise.all([
        axios.get('/api/clients?limit=10000').catch(() => ({ data: {} } as any)),
        axios.get('/api/providers?limit=10000').catch(() => ({ data: {} } as any))
      ]);
      setCtaEntities([
        ...((cr.data?.clients || []).map((c: any) => ({
          id: c.id,
          displayName: c.companyName?.trim() || `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Cliente',
          entityType: 'client'
        }))),
        ...((pr.data?.providers || []).map((p: any) => ({
          id: p.id,
          displayName: p.fantasyName?.trim() || p.name?.trim() || p.companyName?.trim() || 'Proveedor',
          entityType: 'provider'
        })))
      ]);
    } catch {
      // silencioso
    }
  };

  const fetchProviders = async () => {
    try {
      const res = await axios.get('/api/providers?limit=500');
      const list = Array.isArray(res.data) ? res.data : (res.data?.providers || res.data?.data || []);
      setProviders(list.map((p: any) => ({ id: p.id, name: p.name, companyName: p.companyName })));
    } catch {
      // silencioso, no es crítico
    }
  };

  const toggleSelected = (checkId: string) => {
    setSelectedCheckIds((prev) => {
      const next = new Set(prev);
      if (next.has(checkId)) next.delete(checkId);
      else next.add(checkId);
      return next;
    });
  };

  const clearSelection = () => setSelectedCheckIds(new Set());

  const openEndorseDialog = () => {
    if (providers.length === 0) fetchProviders();
    setEndorseProviderId('');
    setEndorseNotes('');
    setEndorseDialogOpen(true);
  };

  const handleEndorseBatch = async () => {
    if (!endorseProviderId) {
      toast.error('Seleccioná un proveedor');
      return;
    }
    const ids = Array.from(selectedCheckIds);
    if (ids.length === 0) {
      toast.error('No hay cheques seleccionados');
      return;
    }
    setEndorsingNow(true);
    try {
      const res = await axios.post('/api/checks/endorse-batch', {
        checkIds: ids,
        providerId: endorseProviderId,
        notes: endorseNotes || undefined,
      });
      const { checkCount, totalAmount, providerName } = res.data || {};
      toast.success(`${checkCount} cheque(s) endosado(s) a ${providerName} por ${formatCurrency(totalAmount || 0)}`);
      ids.forEach((id) => applyLocalCheck(id, { status: 'endorsed' }));
      clearSelection();
      setEndorseDialogOpen(false);
      fetchChecks({ silent: true });
    } catch (error: any) {
      console.error('Error endosando cheques:', error);
      toast.error(error?.response?.data?.error || 'Error al endosar los cheques');
    } finally {
      setEndorsingNow(false);
    }
  };

  const handleEndorse = async () => {
    if (!endorsingCheck || !endorseAccountId) {
      toast.error('Elegí a quién se le endosa el cheque');
      return;
    }
    try {
      setEndorsing(true);
      const body = endorseAccountId.startsWith('entity:')
        ? { entityId: endorseAccountId.split(':')[2], entityType: 'provider', applyToAccount: endorseApply }
        : { accountId: endorseAccountId, applyToAccount: endorseApply };
      const res = await axios.post(`/api/checks/${endorsingCheck.id}/endorse`, body);
      if (res.data?.accountPayment?.applied) {
        toast.success('Cheque endosado — pago registrado en la cta cte del proveedor');
      } else {
        toast.success('Cheque endosado');
      }
      if (res.data?.check) applyLocalCheck(endorsingCheck.id, res.data.check);
      else applyLocalCheck(endorsingCheck.id, { status: 'endorsed' });
      setEndorsingCheck(null);
      fetchChecks({ silent: true });
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo endosar el cheque');
    } finally {
      setEndorsing(false);
    }
  };

  const handleRevertEndorsement = async (check: CheckRecord) => {
    if (!await confirmDialog(`¿Revertir el endoso del cheque #${check.checkNumber || check.id}? Se generará un cargo compensatorio en la cta cte del proveedor.`)) return;
    try {
      await axios.post(`/api/checks/${check.id}/revert-endorsement`);
      applyLocalCheck(check.id, { status: 'pending', endorsedToName: null });
      toast.success('Endoso revertido');
      fetchChecks({ silent: true });
    } catch (error: any) {
      console.error('Error revirtiendo endoso:', error);
      toast.error(error?.response?.data?.error || 'Error al revertir');
    }
  };

  const handleExport = async (type: CheckType | 'all') => {
    try {
      const params = new URLSearchParams();
      if (type !== 'all') params.append('type', type);
      params.append('orderDir', 'asc');
      const response = await axios.get(`/api/checks/export?${params.toString()}`, {
        responseType: 'blob',
      });
      const fileUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      const filename =
        type === 'all'
          ? `cheques-${Date.now()}.xlsx`
          : `cheques-${type}-${Date.now()}.xlsx`;
      link.href = fileUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(fileUrl);
    } catch (error) {
      console.error('Error exportando cheques:', error);
      toast.error('No pudimos exportar los cheques');
    }
  };

  const handleSyncSheet = async () => {
    if (!sheetConfig.enabled) return;
    try {
      setSyncingSheet(true);
      await axios.post('/api/checks/sync-sheet');
      toast.success('Datos sincronizados con Google Sheets');
    } catch (error: any) {
      console.error('Error sincronizando Google Sheets:', error);
      toast.error(
        error?.response?.data?.error || 'No pudimos sincronizar con Google Sheets'
      );
    } finally {
      setSyncingSheet(false);
    }
  };

  const generatePDF = async () => {
    try {
      const response = await fetch('/api/checks/generate-pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const filename = `Cheques-${new Date().toISOString().split('T')[0]}.pdf`;
        const result = openPdf({ blob, filename });
        toast.success(result === 'downloaded' ? 'PDF descargado' : 'PDF abriendo en nueva pestaña');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error en la respuesta del servidor');
      }
    } catch (error: any) {
      console.error('Error generando PDF:', error);
      toast.error(`Error al generar el PDF: ${error.message}`);
    }
  };

  const renderTable = (type: CheckType) => {
    const baseChecks = type === 'received' ? receivedChecks : issuedChecks;
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const preFiltered = baseChecks.filter((check) => {
      if (bankFilter !== 'all' && String(check.bank || '').trim() !== bankFilter) return false;
      if (dueFilter !== 'all') {
        const days = getDaysToDue(check.dueDate);
        const activo = !['cleared', 'rejected', 'repudiated'].includes(check.status);
        if (dueFilter === 'vencidos' && !(days !== null && days < 0 && activo)) return false;
        if (dueFilter === '7' && !(days !== null && days >= 0 && days <= 7 && activo)) return false;
        if (dueFilter === '30' && !(days !== null && days >= 0 && days <= 30 && activo)) return false;
      }
      return true;
    });
    const checks = normalizedSearch
      ? preFiltered.filter((check) => {
          const haystack = [
            check.checkNumber,
            check.emitterName,
            check.clientName,
            check.providerName,
            check.contactName,
            check.bank,
            check.originType,
            check.originNumber,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return haystack.includes(normalizedSearch);
        })
      : preFiltered;
    const title = type === 'received' ? 'Cheques Recibidos' : 'Cheques Emitidos';
    // En emitidos, "pending" no está "en cartera" (ya se lo diste al proveedor): mostrar "Pendiente".
    const statusLabelCtx = (status: string) => (type === 'issued' && status === 'pending') ? 'Pendiente' : statusLabel(status);
    const bancos = Array.from(new Set(baseChecks.map((c) => String(c.bank || '').trim()).filter(Boolean))).sort();
    const summary = (() => {
      const list = baseChecks;
      const totalChecks = list.length;
      const pendingAmount = list
        .filter((check) => check.status === 'pending')
        .reduce((sum, check) => sum + (check.amount || 0), 0);
      const clearedAmount = list
        .filter((check) => check.status === 'cleared')
        .reduce((sum, check) => sum + (check.amount || 0), 0);
      const rejectedCount = list.filter((check) => check.status === 'rejected').length;
      const overdueChecks = list.filter((check) => {
        const days = getDaysToDue(check.dueDate);
        return days !== null && days < 0 && !['cleared', 'rejected', 'repudiated'].includes(check.status);
      });
      const overdueAmount = overdueChecks.reduce((sum, check) => sum + (check.amount || 0), 0);
      const dueSoonChecks = list.filter((check) => {
        const days = getDaysToDue(check.dueDate);
        return days !== null && days >= 0 && days <= 7 && !['cleared', 'rejected', 'repudiated'].includes(check.status);
      });
      const dueTodayCount = list.filter((check) => getDaysToDue(check.dueDate) === 0).length;
      return {
        totalChecks,
        pendingAmount,
        clearedAmount,
        rejectedCount,
        overdueAmount,
        overdueCount: overdueChecks.length,
        dueSoonCount: dueSoonChecks.length,
        dueTodayCount,
      };
    })();

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Total cheques</p>
              <p className="text-lg font-semibold">{summary.totalChecks}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{type === 'received' ? '$ en cartera' : '$ a pagar'}</p>
              <p className="text-lg font-semibold">{formatCurrency(summary.pendingAmount)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">{type === 'received' ? '$ acreditados' : '$ debitados'}</p>
              <p className="text-lg font-semibold">{formatCurrency(summary.clearedAmount)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Rechazados</p>
              <p className="text-lg font-semibold">{summary.rejectedCount}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">$ vencidos</p>
              <p className="text-lg font-semibold">{formatCurrency(summary.overdueAmount)}</p>
            </CardContent>
          </Card>
        </div>

        {(summary.overdueCount > 0 || summary.dueSoonCount > 0) && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 flex flex-wrap items-center gap-2">
            <span className="font-medium">Alertas de plazos:</span>
            {summary.dueTodayCount > 0 && (
              <Badge className="bg-red-100 text-red-800">Vencen hoy: {summary.dueTodayCount}</Badge>
            )}
            {summary.dueSoonCount > 0 && (
              <Badge className="bg-amber-100 text-amber-800">Vencen en 7 dias: {summary.dueSoonCount}</Badge>
            )}
            {summary.overdueCount > 0 && (
              <Badge className="bg-red-100 text-red-800">Vencidos: {summary.overdueCount}</Badge>
            )}
          </div>
        )}

        {type === 'received' && selectedCheckIds.size > 0 && (() => {
          const selectedChecks = receivedChecks.filter((c) => selectedCheckIds.has(c.id));
          const total = selectedChecks.reduce((s, c) => s + (c.amount || 0), 0);
          return (
            <div className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 flex flex-wrap items-center gap-3 text-sm">
              <span className="font-medium">
                {selectedCheckIds.size} cheque{selectedCheckIds.size !== 1 ? 's' : ''} seleccionado{selectedCheckIds.size !== 1 ? 's' : ''}
                {' · '}
                Total: <span className="font-semibold">{formatCurrency(total)}</span>
              </span>
              <div className="ml-auto flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={clearSelection}>
                  Limpiar
                </Button>
                <Button size="sm" onClick={openEndorseDialog}>
                  Endosar a proveedor
                </Button>
              </div>
            </div>
          );
        })()}

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600"></div>
              </div>
            ) : checks.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No hay cheques {type === 'received' ? 'recibidos' : 'emitidos'} registrados.</p>
              </div>
            ) : (
              <>
                <div className="space-y-3 md:hidden p-4">
                  {checks.map((check) => {
                    const daysToDue = getDaysToDue(check.dueDate);
                    return (
                      <div key={check.id} className="rounded-lg border border-border p-4 space-y-3 bg-background">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-foreground">
                              {type === 'received' ? (check.clientName || 'Cliente') : (check.providerName || 'Proveedor')}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {type === 'received' ? 'Cheque' : 'Banco'}: {type === 'received' ? (check.checkNumber || '—') : formatBankName(check.bank)}
                            </p>
                          </div>
                          <Badge className={cn('w-fit', STATUS_BADGES[check.status] || 'bg-slate-100 text-slate-800')}>
                            {statusLabelCtx(check.status)}
                            {check.status === 'endorsed' && check.endorsedToName ? ` a ${check.endorsedToName}` : ''}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
                          <div>
                            <p className="text-[11px] uppercase">Emisión</p>
                            <p className="text-sm text-foreground">{formatDate(check.issueDate)}</p>
                          </div>
                          <div>
                            <p className="text-[11px] uppercase">Cobro</p>
                            <p className="text-sm text-foreground">{formatDate(check.dueDate)}</p>
                          </div>
                          <div>
                            <p className="text-[11px] uppercase">Días a cobrar</p>
                            <Badge className={cn('w-fit', getDueBadgeClass(daysToDue))}>
                              {formatDaysToDue(daysToDue)}
                            </Badge>
                          </div>
                          <div>
                            <p className="text-[11px] uppercase">Monto</p>
                            <p className="text-sm font-semibold text-foreground">{formatCurrency(check.amount)}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{formatOrigin(check.originType, check.originNumber)}</span>
                          <span>{check.contactPhone || '—'}</span>
                        </div>
                        <div className="flex items-center justify-end gap-2 pt-1">
                          <DropdownMenu>
                            <Tip label="Cambiar estado">
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  disabled={updatingStatusId === check.id}
                                  className="size-8"
                                >
                                  <CheckCircleIcon className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                            </Tip>
                            <DropdownMenuContent align="end">
                              {statusOptions.map((option) => (
                                <DropdownMenuItem
                                  key={option.id}
                                  onSelect={(e) => {
                                    e.preventDefault();
                                    handleStatusChange(check, option.id);
                                  }}
                                  className={cn(check.status === option.id && 'bg-muted')}
                                >
                                  {option.label}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <input
                            type="file"
                            accept="image/*"
                            id={`check-photo-m-${check.id}`}
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) handlePhotoUpload(check, f);
                              e.target.value = '';
                            }}
                          />
                          {check.photoUrl ? (
                            <Tip label="Ver foto del cheque">
                              <a href={check.photoUrl} target="_blank" rel="noopener noreferrer" className="size-7 inline-flex items-center justify-center rounded-md border bg-card hover:bg-accent text-emerald-600">
                                <PhotoIcon className="h-3.5 w-3.5" />
                              </a>
                            </Tip>
                          ) : (
                            <Tip label="Adjuntar foto del cheque">
                              <label htmlFor={`check-photo-m-${check.id}`} className="size-7 inline-flex items-center justify-center rounded-md border bg-card hover:bg-accent cursor-pointer text-muted-foreground">
                                {uploadingPhotoId === check.id ? (
                                  <span className="h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <CameraIcon className="h-3.5 w-3.5" />
                                )}
                              </label>
                            </Tip>
                          )}
                          <Tip label="Editar cheque">
                            <Button
                              variant="outline"
                              size="icon"
                              className="size-8"
                              onClick={() => navigate(`/cash-register/checks/${check.id}/edit`)}
                            >
                              <PencilSquareIcon className="h-4 w-4" />
                            </Button>
                          </Tip>
                          <Tip label="Eliminar cheque">
                            <Button
                              variant="outline"
                              size="icon"
                              className="size-8"
                              disabled={deletingId === check.id}
                              onClick={() => handleDelete(check.id)}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </Button>
                          </Tip>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="hidden md:block">
                  <Table className="w-full text-xs leading-tight">
                  <TableHeader>
                    <TableRow className="hover:bg-muted/50 border-b">
                    {type === 'received' && (
                      <TableHead className="text-foreground h-9 px-2 align-middle font-medium text-center w-[36px]">
                        <input
                          type="checkbox"
                          aria-label="Seleccionar todos los cheques en cartera"
                          checked={(() => {
                            const eligible = checks.filter((c) => c.status === 'pending');
                            return eligible.length > 0 && eligible.every((c) => selectedCheckIds.has(c.id));
                          })()}
                          onChange={(e) => {
                            const eligible = checks.filter((c) => c.status === 'pending');
                            if (e.target.checked) {
                              setSelectedCheckIds((prev) => {
                                const next = new Set(prev);
                                eligible.forEach((c) => next.add(c.id));
                                return next;
                              });
                            } else {
                              setSelectedCheckIds((prev) => {
                                const next = new Set(prev);
                                eligible.forEach((c) => next.delete(c.id));
                                return next;
                              });
                            }
                          }}
                        />
                      </TableHead>
                    )}
                    <TableHead className="text-foreground h-9 px-2 align-middle font-medium text-left w-[90px] whitespace-nowrap cq-hide-narrow">F. Emisión</TableHead>
                    <TableHead className="text-foreground h-9 px-2 align-middle font-medium text-left w-[90px] whitespace-nowrap">F. Cobro</TableHead>
                    <TableHead className="text-foreground h-9 px-2 align-middle font-medium text-left w-[88px]">Vence en</TableHead>
                    {type === 'received' ? (
                      <TableHead className="text-foreground h-9 px-2 align-middle font-medium text-left w-[92px]">N° Cheque</TableHead>
                    ) : (
                      <TableHead className="text-foreground h-9 px-2 align-middle font-medium text-left w-[92px]">Monto</TableHead>
                    )}
                    {type === 'received' ? (
                      <TableHead className="text-foreground h-9 px-2 align-middle font-medium text-left w-[120px] whitespace-nowrap">Cliente</TableHead>
                    ) : (
                      <TableHead className="text-foreground h-9 px-2 align-middle font-medium text-left w-[120px] whitespace-nowrap">Proveedor</TableHead>
                    )}
                    {type === 'received' ? (
                      <TableHead className="text-foreground h-9 px-2 align-middle font-medium text-left w-[92px]">Monto</TableHead>
                    ) : (
                      <TableHead className="text-foreground h-9 px-2 align-middle font-medium text-left w-[92px]">Banco</TableHead>
                    )}
                    {type === 'received' ? (
                      <TableHead className="text-foreground h-9 px-2 align-middle font-medium text-left w-[92px]">Banco</TableHead>
                    ) : null}
                    <TableHead className="text-foreground h-9 px-2 align-middle font-medium text-left w-[110px] whitespace-nowrap cq-hide-narrow">Origen</TableHead>
                    <TableHead className="text-foreground h-9 px-2 align-middle font-medium text-left w-[100px]">Estado</TableHead>
                    <TableHead className="text-foreground h-9 px-2 text-right align-middle font-medium w-[132px]">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {checks.map((check) => {
                    const daysToDue = getDaysToDue(check.dueDate);
                    return (
                      <TableRow key={check.id} className="border-b hover:bg-muted/50">
                        {type === 'received' && (
                          <TableCell className="px-2 py-2 align-middle text-center" onClick={(e) => e.stopPropagation()}>
                            {check.status === 'pending' ? (
                              <input
                                type="checkbox"
                                aria-label={`Seleccionar cheque ${check.checkNumber || ''}`}
                                checked={selectedCheckIds.has(check.id)}
                                onChange={() => toggleSelected(check.id)}
                              />
                            ) : null}
                          </TableCell>
                        )}
                        <TableCell className="px-2 py-2 align-middle whitespace-nowrap text-left cq-hide-narrow">
                          <div>{formatDate(check.issueDate)}</div>
                        </TableCell>
                        <TableCell className="px-2 py-2 align-middle whitespace-nowrap text-left">
                          <div>{formatDate(check.dueDate)}</div>
                        </TableCell>
                        <TableCell className="px-2 py-2 align-middle text-left">
                          <Badge
                            className={cn(
                              getDueBadgeClass(daysToDue),
                              'max-w-[72px] whitespace-normal break-words text-center leading-tight justify-center'
                            )}
                          >
                            {formatDaysToDue(daysToDue)}
                          </Badge>
                        </TableCell>
                        {type === 'received' ? (
                          <TableCell className="px-2 py-2 align-middle text-left">
                            <Tip label={check.checkNumber || ''}>
                              <div className="max-w-[92px] truncate">{check.checkNumber || '—'}</div>
                            </Tip>
                          </TableCell>
                        ) : (
                          <TableCell className="px-2 py-2 align-middle whitespace-nowrap text-left">
                            <div className="font-semibold">{formatCurrency(check.amount)}</div>
                          </TableCell>
                        )}
                        {type === 'received' ? (
                          <TableCell className="px-2 py-2 align-middle text-left">
                            <Tip label={check.clientName || ''}>
                              <div className="max-w-[120px] truncate">
                                {check.clientName || '—'}
                              </div>
                            </Tip>
                          </TableCell>
                        ) : (
                          <TableCell className="px-2 py-2 align-middle text-left">
                            <Tip label={check.providerName || ''}>
                              <div className="max-w-[120px] truncate">
                                {check.providerName || '—'}
                              </div>
                            </Tip>
                          </TableCell>
                        )}
                        {type === 'received' ? (
                          <TableCell className="px-2 py-2 align-middle whitespace-nowrap text-left">
                            <div className="font-semibold">{formatCurrency(check.amount)}</div>
                          </TableCell>
                        ) : (
                          <TableCell className="px-2 py-2 align-middle text-left">
                            <Tip label={formatBankName(check.bank)}>
                              <div className="max-w-[92px] truncate">
                                {formatBankName(check.bank)}
                              </div>
                            </Tip>
                          </TableCell>
                        )}
                        {type === 'received' ? (
                          <TableCell className="px-2 py-2 align-middle text-left">
                            <Tip label={formatBankName(check.bank)}>
                              <div className="max-w-[92px] truncate">
                                {formatBankName(check.bank)}
                              </div>
                            </Tip>
                          </TableCell>
                        ) : null}
                        <TableCell className="px-2 py-2 align-middle text-left cq-hide-narrow">
                          {(() => {
                            const originText = formatOrigin(check.originType, check.originNumber);
                            return (
                              <Tip label={originText}>
                                <div className="max-w-[110px] truncate">
                                  {originText}
                                </div>
                              </Tip>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="px-2 py-2 align-middle whitespace-nowrap text-left">
                          <Tip label={check.status === 'endorsed' && check.endorsedToName ? `Endosado a ${check.endorsedToName}` : statusLabelCtx(check.status)}>
                            <div className="w-fit">
                              <Badge
                                className={cn(
                                  'w-fit',
                                  STATUS_BADGES[check.status] || 'bg-slate-100 text-slate-800'
                                )}
                              >
                                {statusLabelCtx(check.status)}
                              </Badge>
                            </div>
                          </Tip>
                        </TableCell>
                        <TableCell className="px-2 py-2 align-middle whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                              <DropdownMenu>
                                <Tip label="Cambiar estado">
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      disabled={updatingStatusId === check.id}
                                      className="size-7"
                                    >
                                      <CheckCircleIcon className="h-3.5 w-3.5" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                </Tip>
                                <DropdownMenuContent align="end">
                                  {statusOptions.map((option) => (
                                    <DropdownMenuItem
                                      key={option.id}
                                      onSelect={(e) => {
                                        e.preventDefault();
                                        handleStatusChange(check, option.id);
                                      }}
                                      className={cn(
                                        check.status === option.id && 'bg-muted'
                                      )}
                                    >
                                      {option.label}
                                    </DropdownMenuItem>
                                  ))}
                                  {check.status === 'endorsed' && type === 'received' && (
                                    <DropdownMenuItem
                                      onSelect={(e) => {
                                        e.preventDefault();
                                        handleRevertEndorsement(check);
                                      }}
                                      className="text-amber-700 focus:text-amber-800"
                                    >
                                      ↩ Revertir endoso
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                              <input
                                type="file"
                                accept="image/*"
                                id={`check-photo-d-${check.id}`}
                                className="hidden"
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  if (f) handlePhotoUpload(check, f);
                                  e.target.value = '';
                                }}
                              />
                              {check.photoUrl ? (
                                <Tip label="Ver foto del cheque">
                                  <a href={check.photoUrl} target="_blank" rel="noopener noreferrer" className="size-7 inline-flex items-center justify-center rounded-md border bg-card hover:bg-accent text-emerald-600">
                                    <PhotoIcon className="h-3.5 w-3.5" />
                                  </a>
                                </Tip>
                              ) : (
                                <Tip label="Adjuntar foto del cheque">
                                  <label htmlFor={`check-photo-d-${check.id}`} className="size-7 inline-flex items-center justify-center rounded-md border bg-card hover:bg-accent cursor-pointer text-muted-foreground">
                                    {uploadingPhotoId === check.id ? (
                                      <span className="h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                      <CameraIcon className="h-3.5 w-3.5" />
                                    )}
                                  </label>
                                </Tip>
                              )}
                              <Tip label="Editar cheque">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/cash-register/checks/${check.id}/edit`);
                                  }}
                                  className="size-7"
                                >
                                  <PencilSquareIcon className="h-3.5 w-3.5" />
                                </Button>
                              </Tip>
                              <Tip label="Eliminar cheque">
                                <Button
                                  variant="outline"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(check.id);
                                  }}
                                  disabled={deletingId === check.id}
                                  className="size-7"
                                >
                                  <TrashIcon className="h-3.5 w-3.5" />
                                </Button>
                              </Tip>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                  </Table>
                </div>
              </>
            )}
        </CardContent>
      </Card>
      </div>
    );
  };

  return (
    <div className="space-y-6 w-full max-w-none mx-auto p-6">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <button onClick={() => navigate('/dashboard')} className="text-left">
                Dashboard
              </button>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Seguimiento de Cheques</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <h1 className="text-2xl font-semibold">Seguimiento de Cheques</h1>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        <Tabs
          value={activeTable}
          onValueChange={(value) => {
            setActiveTable(value as CheckType);
            clearSelection();
            if (value === 'received' && providers.length === 0) fetchProviders();
          }}
          className="w-full sm:w-auto sm:flex-none"
        >
          <TabsList className="inline-flex h-8 w-full items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground">
            <TabsTrigger
              value="received"
              className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Cheques Recibidos
            </TabsTrigger>
            <TabsTrigger
              value="issued"
              className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow"
            >
              <DocumentArrowDownIcon className="h-4 w-4" />
              Cheques Emitidos
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2 w-full sm:w-auto">
              <ArrowDownTrayIcon className="h-4 w-4" />
              Exportar Excel
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem
              onSelect={(event) => {
                event.preventDefault();
                handleExport('all');
              }}
            >
              Todos
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(event) => {
                event.preventDefault();
                handleExport('received');
              }}
            >
              Recibidos
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(event) => {
                event.preventDefault();
                handleExport('issued');
              }}
            >
              Emitidos
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          variant="outline"
          onClick={generatePDF}
          className="gap-2 w-full sm:w-auto"
        >
          <DocumentArrowDownIcon className="h-4 w-4" />
          Generar PDF
        </Button>

        <Button
          onClick={() => navigate(`/cash-register/checks/new?type=${activeTable}`)}
          className="gap-2 w-full sm:w-auto bg-black dark:bg-white text-white dark:text-black hover:bg-black/90 dark:hover:bg-white/90"
        >
          <PlusIcon className="h-4 w-4" />
          Nuevo cheque
        </Button>

        {sheetConfig.sheetUrl && (
          <Button asChild variant="ghost" className="gap-2">
            <a href={sheetConfig.sheetUrl} target="_blank" rel="noopener noreferrer">
              <LinkIcon className="h-4 w-4" />
              Abrir Google Sheets
            </a>
          </Button>
        )}

        {sheetConfig.enabled && (
          <Button
            variant="ghost"
            className="gap-2"
            onClick={handleSyncSheet}
            disabled={syncingSheet}
          >
            <ArrowPathIcon
              className={cn(
                'h-4 w-4',
                syncingSheet ? 'animate-spin text-primary' : 'text-muted-foreground'
              )}
            />
            {syncingSheet ? 'Sincronizando...' : 'Sincronizar Sheets'}
          </Button>
        )}
      </div>

      <div className="relative w-full md:w-1/2">
        <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
        <Input
          type="text"
          placeholder="Buscar por número, banco o contacto..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          className="pl-8 h-9"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <Select value={bankFilter} onValueChange={setBankFilter}>
          <SelectTrigger className="h-9 w-[180px]"><SelectValue placeholder="Banco" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los bancos</SelectItem>
            {Array.from(new Set([...receivedChecks, ...issuedChecks].map((c) => String(c.bank || '').trim()).filter(Boolean))).sort().map((b) => (
              <SelectItem key={b} value={b}>{b}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={dueFilter} onValueChange={setDueFilter}>
          <SelectTrigger className="h-9 w-[190px]"><SelectValue placeholder="Vencimiento" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los vencimientos</SelectItem>
            <SelectItem value="vencidos">Vencidos sin resolver</SelectItem>
            <SelectItem value="7">Vencen en 7 días</SelectItem>
            <SelectItem value="30">Vencen en 30 días</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {sheetConfig.embedUrl && (
        <div className="rounded-xl border border-border">
          <div className="border-b px-4 py-2">
            <p className="text-sm font-medium text-muted-foreground">
              Vista rápida de Google Sheets
            </p>
          </div>
          <div className="h-[320px] w-full">
            <iframe
              title="Google Sheets Cheques"
              src={sheetConfig.embedUrl}
              className="h-full w-full border-0"
              loading="lazy"
            />
          </div>
        </div>
      )}

      <div>{renderTable(activeTable)}</div>

      {/* Endoso: elegir a quién se le pasa el cheque */}
      <Dialog open={!!endorsingCheck} onOpenChange={(open) => { if (!open) setEndorsingCheck(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Endosar cheque N° {endorsingCheck?.checkNumber || 's/n'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Proveedor al que se le entrega</Label>
              <AccountCombobox
                accounts={(() => {
                  const withAccount = ctaAccounts.filter((a) => a.entityType === 'provider');
                  const covered = new Set(withAccount.map((a) => a.entityId));
                  const withoutAccount = ctaEntities
                    .filter((e) => e.entityType === 'provider' && !covered.has(e.id))
                    .map((e) => ({ id: `entity:provider:${e.id}`, displayName: e.displayName, currentBalance: null as number | null }))
                    .sort((a, b) => a.displayName.localeCompare(b.displayName, 'es'));
                  return [...withAccount, ...withoutAccount];
                })()}
                value={endorseAccountId}
                onChange={setEndorseAccountId}
                balanceLabel="le debés"
                placeholder="Buscar proveedor..."
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={endorseApply} onChange={(e) => setEndorseApply(e.target.checked)} className="h-4 w-4" />
              Registrar como pago en su cta cte (baja tu deuda por {formatCurrency(endorsingCheck?.amount || 0)})
            </label>
            <p className="text-xs text-muted-foreground">
              Si después revertís el endoso, se genera un cargo compensatorio y el cheque vuelve a cartera.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEndorsingCheck(null)}>Cancelar</Button>
              <Button onClick={handleEndorse} disabled={endorsing || !endorseAccountId}>
                {endorsing ? 'Endosando...' : 'Endosar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCheck ? 'Editar cheque' : 'Registrar nuevo cheque'}
            </DialogTitle>
            <DialogDescription>
              Completá los datos necesarios para hacer el seguimiento del cheque.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Tipo</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => handleFormChange('type', value as CheckType)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Tipo de cheque" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="received">Recibido</SelectItem>
                    <SelectItem value="issued">Emitido</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Estado</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => handleFormChange('status', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {!editingCheck && (
              <div className="rounded-lg border border-dashed p-3 space-y-2">
                <Label className="text-xs">
                  {formData.type === 'received' ? 'Cuenta corriente del cliente (opcional)' : 'Cuenta corriente del proveedor (opcional)'}
                </Label>
                <AccountCombobox
                  accounts={(() => {
                    const entityType = formData.type === 'received' ? 'client' : 'provider';
                    const withAccount = ctaAccounts.filter((a) => a.entityType === entityType);
                    const covered = new Set(withAccount.map((a) => a.entityId));
                    const withoutAccount = ctaEntities
                      .filter((e) => e.entityType === entityType && !covered.has(e.id))
                      .map((e) => ({ id: `entity:${e.entityType}:${e.id}`, displayName: e.displayName, currentBalance: null as number | null }))
                      .sort((a, b) => a.displayName.localeCompare(b.displayName, 'es'));
                    return [...withAccount, ...withoutAccount];
                  })()}
                  value={formAccountId}
                  onChange={setFormAccountId}
                  balanceLabel={formData.type === 'received' ? 'debe' : 'le debés'}
                />
                {formAccountId && (
                  <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={applyToAccount}
                      onChange={(e) => setApplyToAccount(e.target.checked)}
                      className="h-3.5 w-3.5"
                    />
                    Registrar como pago en la cuenta corriente ({formData.type === 'received' ? 'baja la deuda del cliente' : 'baja tu deuda con el proveedor'})
                  </label>
                )}
                <p className="text-[11px] text-muted-foreground">
                  Si el cheque después rebota, la deuda se vuelve a cargar sola en la cuenta.
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Fecha de emisión</Label>
                <DatePicker
                  value={formData.issueDate}
                  onChange={(v) => handleFormChange('issueDate', v)}
                />
              </div>
              {formData.type === 'received' ? (
                <div>
                  <Label>Fecha de recepción</Label>
                  <DatePicker
                    value={formData.receivedDate}
                    onChange={(v) => handleFormChange('receivedDate', v)}
                  />
                </div>
              ) : (
                <div>
                  <Label>Fecha de entrega</Label>
                  <DatePicker
                    value={formData.deliveryDate}
                    onChange={(v) => handleFormChange('deliveryDate', v)}
                  />
                </div>
              )}
              <div>
                <Label>Fecha de cobro *</Label>
                <DatePicker
                  value={formData.dueDate}
                  onChange={(v) => handleFormChange('dueDate', v)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Número de cheque</Label>
                <Input
                  value={formData.checkNumber}
                  onChange={(event) => handleFormChange('checkNumber', event.target.value)}
                />
              </div>
              <div>
                <Label>Banco</Label>
                <BankCombobox value={formData.bank} onChange={(v) => handleFormChange('bank', v)} />
              </div>
            </div>

            <div>
              <Label>Monto *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                required
                value={formData.amount}
                onChange={(event) => handleFormChange('amount', event.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Origen</Label>
                <Select
                  value={formData.originType || 'none'}
                  onValueChange={(value) => handleFormChange('originType', value === 'none' ? '' : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin origen</SelectItem>
                    <SelectItem value="remito">Remito</SelectItem>
                    <SelectItem value="factura">Factura</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Numero origen</Label>
                <Input
                  value={formData.originNumber}
                  onChange={(event) => handleFormChange('originNumber', event.target.value)}
                />
              </div>
            </div>

            {formData.type === 'received' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Emisor</Label>
                  <Input
                    value={formData.emitterName}
                    onChange={(event) => handleFormChange('emitterName', event.target.value)}
                  />
                </div>
                <div>
                  <Label>Cliente</Label>
                  <Input
                    value={formData.clientName}
                    onChange={(event) => handleFormChange('clientName', event.target.value)}
                  />
                </div>
              </div>
            ) : (
              <div>
                <Label>Proveedor</Label>
                <Input
                  value={formData.providerName}
                  onChange={(event) => handleFormChange('providerName', event.target.value)}
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Contacto</Label>
                <Input
                  value={formData.contactName}
                  onChange={(event) => handleFormChange('contactName', event.target.value)}
                />
              </div>
              <div>
                <Label>Teléfono / Email</Label>
                <Input
                  value={formData.contactPhone}
                  onChange={(event) => handleFormChange('contactPhone', event.target.value)}
                />
              </div>
            </div>

            <div>
              <Label>Notas</Label>
              <Textarea
                rows={3}
                value={formData.notes}
                onChange={(event) => handleFormChange('notes', event.target.value)}
              />
            </div>

            <div className="flex items-center justify-end gap-3">
              <Button type="button" variant="ghost" onClick={() => setFormOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Guardando...' : editingCheck ? 'Guardar cambios' : 'Registrar cheque'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={endorseDialogOpen} onOpenChange={setEndorseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Endosar cheques a proveedor</DialogTitle>
            <DialogDescription>
              {selectedCheckIds.size} cheque{selectedCheckIds.size !== 1 ? 's' : ''} seleccionado{selectedCheckIds.size !== 1 ? 's' : ''}.
              Se generará un movimiento de pago en la cta cte del proveedor por cada cheque.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {(() => {
              const selected = receivedChecks.filter((c) => selectedCheckIds.has(c.id));
              const total = selected.reduce((s, c) => s + (c.amount || 0), 0);
              return (
                <>
                  <div className="rounded-md border bg-muted/30 p-3 max-h-[160px] overflow-auto space-y-1.5 text-xs">
                    {selected.map((c) => (
                      <div key={c.id} className="flex justify-between gap-2">
                        <span className="truncate">
                          #{c.checkNumber || '?'} · {formatBankName(c.bank)} · Vto {formatDate(c.dueDate)}
                        </span>
                        <span className="font-medium whitespace-nowrap">{formatCurrency(c.amount)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center text-sm font-semibold">
                    <span>Total a aplicar:</span>
                    <span>{formatCurrency(total)}</span>
                  </div>
                </>
              );
            })()}

            <div>
              <Label>Proveedor</Label>
              <Select
                value={endorseProviderId}
                onValueChange={(v) => setEndorseProviderId(v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar proveedor" />
                </SelectTrigger>
                <SelectContent>
                  {providers.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">Cargando proveedores...</div>
                  ) : (
                    providers.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.companyName || p.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Nota (opcional)</Label>
              <Textarea
                value={endorseNotes}
                onChange={(e) => setEndorseNotes(e.target.value)}
                rows={2}
                placeholder="Referencia interna, comprobante, etc."
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEndorseDialogOpen(false)} disabled={endorsingNow}>
                Cancelar
              </Button>
              <Button onClick={handleEndorseBatch} disabled={endorsingNow || !endorseProviderId}>
                {endorsingNow ? 'Endosando...' : 'Confirmar endoso'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CashChecks;
